/**
 * What only an administrator can ask for.
 *
 * Every endpoint here is `IsAdminUser` on the backend, which is the check that
 * matters -- the route guard keeps the page off the screen, and this is what
 * would refuse a request made without one anyway.
 */
import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'

import { getApi, unwrap } from '@/api/client'
import { qk } from '@/api/query-keys'
import type {
  AdminUser,
  EmailTestResult,
  HealthCheck,
  Settings,
  SettingsUpdate,
  SystemMetrics,
} from '@/api/types'
import { listingQuery, type Listing } from './listing'

/**
 * Whether the services this instance depends on are answering.
 *
 * Never cached: a health check that says what was true a minute ago is worse
 * than no health check, because it is read at exactly the moment somebody
 * suspects something is wrong.
 */
export function healthQuery() {
  return queryOptions({
    queryKey: qk.adminHealth(),
    staleTime: 0,
    gcTime: 0,
    queryFn: (): Promise<HealthCheck> => unwrap(getApi().GET('/api/_health/full/')),
  })
}

export function useHealth() {
  return useQuery(healthQuery())
}

/** How often the health page samples the host while it is open. */
export const SYSTEM_SAMPLE_MS = 10_000

/**
 * What the host and the background workers are doing now.
 *
 * Polled rather than read once, because each answer is also a point on the
 * page's charts. Polling pauses while the tab is hidden, which leaves a gap in
 * the history rather than a line drawn through it.
 */
export function systemQuery() {
  return queryOptions({
    queryKey: qk.adminSystem(),
    staleTime: 0,
    gcTime: 0,
    refetchInterval: SYSTEM_SAMPLE_MS,
    queryFn: (): Promise<SystemMetrics> => unwrap(getApi().GET('/api/_health/system/')),
  })
}

export function useSystem() {
  return useQuery(systemQuery())
}

/** The id this deployment is known by, for a support conversation. */
export function instanceIdQuery() {
  return queryOptions({
    queryKey: qk.instanceId(),
    staleTime: Infinity,
    queryFn: (): Promise<{ instance_id: string }> =>
      unwrap(getApi().GET('/api/settings/instance-id/')),
  })
}

export function useInstanceId() {
  return useQuery(instanceIdQuery())
}

/**
 * Changing an instance setting.
 *
 * **The answer is merged over what is held, never written in its place.** The
 * update endpoint answers with the settings an administrator owns; the cached
 * entry is the *read* endpoint's answer, which carries the `frontend` bucket
 * every page reads its limits from. Storing the answer whole would take those
 * away from the running application until something asked again.
 */
export function useUpdateSettings() {
  const queryClient = useQueryClient()

  const update = useMutation({
    mutationFn: (changes: SettingsUpdate): Promise<SettingsUpdate> =>
      unwrap(getApi().PATCH('/api/settings/update/', { body: changes })),
    onSuccess: (answer) => {
      queryClient.setQueryData<Settings>(qk.settings(), (held) =>
        held === undefined ? held : { ...held, ...answer },
      )
    },
  })

  return {
    update: (changes: SettingsUpdate) => {
      update.mutate(changes)
    },
    isPending: update.isPending,
  }
}

/**
 * Sending a test email.
 *
 * The one place in the application where a failure is the useful answer: the
 * endpoint returns the stack trace rather than an error status, because whoever
 * is reading it is trying to find out why mail does not leave this instance.
 */
export function useEmailTest() {
  return useMutation({
    // Off the global error toast: a refused address is the result being asked
    // for, and it is shown in the page rather than over it.
    meta: { skipGlobalErrorHandler: true },
    mutationFn: (target: string): Promise<EmailTestResult> =>
      unwrap(getApi().POST('/api/_health/email/', { body: { target_email: target } })),
  })
}

/** One page of results, as every admin listing endpoint answers. */
export interface Page<T> {
  count: number
  next: string | null
  previous: string | null
  results: T[]
}

/**
 * One page of accounts.
 *
 * `placeholderData` holds the page that is on screen while the next one is
 * fetched, so paging and searching do not blank the table between answers --
 * which is what makes a search feel like filtering rather than reloading.
 */
export function adminUsersQuery(listing: Listing) {
  const query = listingQuery(listing)
  return queryOptions({
    queryKey: qk.adminUsers(JSON.stringify(query)),
    placeholderData: keepPreviousData,
    queryFn: (): Promise<Page<AdminUser>> =>
      unwrap(getApi().GET('/api/admin/users/', { params: { query } })),
  })
}

export function useAdminUsers(listing: Listing) {
  return useQuery(adminUsersQuery(listing))
}
